import React from "react";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import closeIcon from "@src/assets/svg/close-icon-gray.svg";
import styles from "./ota-confirm-firmware-installation-modal.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { OTASecondaryButton } from "@src/components/atoms/buttons/ota-secondary-button/ota-secondary-button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useOtaConfirmFirmwareInstallationModal } from "./use-ota-confirm-firmware-installation-modal";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";

type ConfirmInstallData = ActiveInstallationProcessPayload;

const cssClassName = "ota-confirm-firmware-installation-modal";

const OTAConfirmFirmwareInstallationModal = ({
  data,
  onClose,
  setInstallAndActionBlock,
}: {
  data: ConfirmInstallData;
  onClose: () => void;
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >;
}) => {
  const {
    firmware,
    machine,
    folders,
    texts,
    handleCloseModal,
    handleDeny,
    handleApprove,
    isApproveLoading,
    target_path,
  } = useOtaConfirmFirmwareInstallationModal(
    data,
    onClose,
    setInstallAndActionBlock,
  );

  return (
    <div className={styles[cssClassName]}>
      <div
        className={styles[`${cssClassName}__overlay`]}
        aria-label={texts.closeModalAriaLabel}
      />
      <div className={styles[`${cssClassName}__dialog-wrapper`]}>
        <div
          className={styles[`${cssClassName}__modal-body`]}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles[`${cssClassName}__header`]}>
            <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>
            <IconButton
              fun={handleCloseModal}
              icon={closeIcon}
              tooltipPosition="top"
              variant="close-button"
              disabled={isApproveLoading}
            />
          </div>

          <div className={styles[`${cssClassName}__form`]}>
            <FlexColumnContainer verticalGap={20} alignItems="stretch">
              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.firmwareSectionTitle}
                </p>
                <div className={styles[`${cssClassName}__info-card`]}>
                  <p className={styles[`${cssClassName}__firmware-name`]}>
                    {firmware.fileName}
                    <span className={styles[`${cssClassName}__firmware-size`]}>
                      ({firmware.fileSize})
                    </span>
                  </p>
                  <p className={styles[`${cssClassName}__meta-text`]}>
                    {texts.uploadedAtLabel} {firmware.uploadedAt}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.machineSectionTitle}
                </p>
                <div className={styles[`${cssClassName}__machine-card`]}>
                  <p className={styles[`${cssClassName}__machine-name`]}>
                    {machine.name}{" "}
                    <span className={styles[`${cssClassName}__machine-ip`]}>
                      ({machine.ip})
                    </span>
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.targetPathLabel}
                </p>
                <div className={styles[`${cssClassName}__folders-list-card`]}>
                  <p className={styles[`${cssClassName}__folder-name`]}>
                    {target_path}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={15} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.foldersSectionTitle}
                </p>
                <div className={styles[`${cssClassName}__folders-list-card`]}>
                  {folders?.map((folder) => (
                    <p
                      key={folder}
                      className={styles[`${cssClassName}__folder-name`]}
                    >
                      \{folder}
                    </p>
                  ))}
                </div>
              </FlexColumnContainer>

              <p className={styles[`${cssClassName}__warning-text`]}>
                {texts.warningText}
              </p>
            </FlexColumnContainer>
          </div>

          <div className={styles[`${cssClassName}__buttons-wrapper`]}>
            <OTASecondaryButton
              fun={handleDeny}
              text={texts.denyButtonText}
              disabled={isApproveLoading}
            />
            <ButtonWithLoader
              fun={handleApprove}
              text={texts.approveButtonText}
              loading={isApproveLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTAConfirmFirmwareInstallationModal;
